// Table: a header row, rows, and a row that expands to a detail row beneath it. It scrolls
// sideways inside its own bordered container, never the page.
import { useId, useState, type ComponentProps, type ReactNode } from 'react';
import { cn } from './cn';

export function Table({ className, children, ...props }: ComponentProps<'table'>) {
  return (
    // relative: an absolutely placed child (an sr-only header) stays inside the scroll box.
    <div className="relative max-w-full overflow-x-auto rounded-lg border border-hairline">
      <table className={cn('w-full border-collapse text-[12.5px]', className)} {...props}>
        {children}
      </table>
    </div>
  );
}

export function Th({ className, scope = 'col', ...props }: ComponentProps<'th'>) {
  return (
    <th
      scope={scope}
      className={cn(
        'border-b border-hairline bg-surface px-3.5 py-[9px] text-left text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-muted',
        className,
      )}
      {...props}
    />
  );
}

export function Td({ className, ...props }: ComponentProps<'td'>) {
  return (
    <td
      className={cn('border-b border-hairline px-3.5 py-2.5 align-middle text-ink-display [tr:last-child>&]:border-b-0', className)}
      {...props}
    />
  );
}

/** A row with a toggle in its first cell; open, a full-width detail row follows it. `dimmed`
 * fades the row's cells (a superseded run). */
export function ExpandableRow({
  cells,
  detail,
  label,
  defaultOpen = false,
  dimmed = false,
  ...props
}: {
  cells: ReactNode[];
  detail: ReactNode;
  label: string;
  defaultOpen?: boolean;
  dimmed?: boolean;
} & Omit<ComponentProps<'tr'>, 'children'>) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <>
      <tr className={cn(dimmed && '[&>td]:opacity-60')} {...props}>
        <Td className="w-8 pr-0">
          <button
            type="button"
            className="inline-flex size-6 cursor-pointer items-center justify-center rounded text-ink-muted hover:bg-surface hover:text-ink-display"
            aria-expanded={open}
            aria-controls={id}
            aria-label={`${open ? 'Hide' : 'Show'} ${label}`}
            onClick={() => setOpen(!open)}
          >
            <svg viewBox="0 0 16 16" className={cn('size-3 transition-transform', open && 'rotate-90')} aria-hidden="true">
              <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </Td>
        {cells.map((c, i) => (
          <Td key={i}>{c}</Td>
        ))}
      </tr>
      {open && (
        <tr id={id}>
          <Td colSpan={cells.length + 1} className="bg-surface text-ink-body">
            {detail}
          </Td>
        </tr>
      )}
    </>
  );
}
